import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  App,
  Avatar,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Empty,
  Modal,
  Select,
  Spin,
  TimePicker,
  Typography,
} from "antd";
import Dayjs, { type Dayjs as DayjsType } from "dayjs";
import { ReloadOutlined, UserOutlined } from "../components/icons";
import {
  DateMultiPicker,
  defaultRecentExportDateKeys,
} from "../components/groupChat/DateMultiPicker";
import { trpc } from "../trpc";

const { Text, Title } = Typography;

type AnyRecord = Record<string, any>;

const GROUP_CHAT_UNIT_KEY = "groupChatBusinessUnitId";
const PAGE_LIMIT = 50;
const TIME_GAP_MS = 5 * 60 * 1000;
const mediaPreviewTypes = new Set(["image", "emotion"]);

function archiveMediaUrl(record: {
  sourceId?: string;
  mediaMd5?: string;
  msgtype?: string;
}) {
  if (!record.sourceId || !record.mediaMd5 || !mediaPreviewTypes.has(record.msgtype || "")) {
    return "";
  }
  return `/api/archive-media/${encodeURIComponent(record.sourceId)}/${encodeURIComponent(record.mediaMd5)}`;
}

function formatMessageTime(value: string | null | undefined) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("zh-CN", {
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function sortByTimeAsc(items: AnyRecord[]) {
  return [...items].sort((a, b) => {
    const ta = new Date(a.messageTime || 0).getTime();
    const tb = new Date(b.messageTime || 0).getTime();
    if (ta !== tb) return ta - tb;
    return String(a.id || "").localeCompare(String(b.id || ""));
  });
}

function mergeUniqueById(existing: AnyRecord[], incoming: AnyRecord[]) {
  const map = new Map<string, AnyRecord>();
  for (const item of [...existing, ...incoming]) {
    const id = String(item.id || item.msgid || "");
    if (!id) continue;
    map.set(id, item);
  }
  return sortByTimeAsc(Array.from(map.values()));
}

function messageBodyText(record: AnyRecord) {
  const isMedia = mediaPreviewTypes.has(record.msgtype || "");
  if (isMedia) {
    return record.matchKey || record.content || record.normalizedContent || "";
  }
  return record.content || record.normalizedContent || record.matchKey || "";
}

function dayRangeISO(date: DayjsType | null) {
  if (!date) return { startTime: undefined as string | undefined, endTime: undefined as string | undefined };
  return {
    startTime: date.startOf("day").toISOString(),
    endTime: date.endOf("day").toISOString(),
  };
}

function downloadBase64Xlsx(base64: string, fileName: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function GroupChatHistoryPage() {
  const { message } = App.useApp();
  const [units, setUnits] = useState<AnyRecord[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>(() => {
    return localStorage.getItem(GROUP_CHAT_UNIT_KEY) || undefined;
  });
  const [selectedRoomId, setSelectedRoomId] = useState<string | undefined>();
  const [messageDate, setMessageDate] = useState<DayjsType | null>(() => Dayjs());
  const [exportDates, setExportDates] = useState<string[]>(() => defaultRecentExportDateKeys(5));
  /** "HH:mm" 或 null（未设门槛）；默认 07:30 可清空 */
  const [exportAfterTime, setExportAfterTime] = useState<string | null>("07:30");
  /** 导出消息类型，默认仅文字 */
  const [exportMsgtypes, setExportMsgtypes] = useState<Array<"text" | "image">>([
    "text",
  ]);
  const [exporting, setExporting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [messages, setMessages] = useState<AnyRecord[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [mediaModal, setMediaModal] = useState<{ url: string; title: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const prevScrollHeightRef = useRef(0);

  const dateRange = useMemo(() => dayRangeISO(messageDate), [messageDate]);

  const selectedUnit = useMemo(
    () => units.find((unit) => unit.id === selectedUnitId),
    [units, selectedUnitId]
  );

  const roomOptions = useMemo(() => {
    const rooms = (selectedUnit?.groups || []).flatMap((group: AnyRecord) =>
      (group.rooms || []).map((room: AnyRecord) => ({
        label: room.roomName || room.roomId,
        value: room.roomId,
        groupName: group.name || "",
        enabled: room.enabled !== false,
      }))
    );
    const seen = new Set<string>();
    return rooms
      .filter((room: { value: string }) => {
        if (!room.value || seen.has(room.value)) return false;
        seen.add(room.value);
        return true;
      })
      .map((room: { label: string; value: string; groupName: string; enabled: boolean }) => ({
        label: room.groupName
          ? `${room.label}${room.enabled ? "" : "（已停用）"} · ${room.groupName}`
          : `${room.label}${room.enabled ? "" : "（已停用）"}`,
        value: room.value,
      }))
      .sort((a: { label: string }, b: { label: string }) =>
        a.label.localeCompare(b.label, "zh-CN")
      );
  }, [selectedUnit]);

  const selectedRoomLabel = useMemo(() => {
    const hit = roomOptions.find((item: { value: string }) => item.value === selectedRoomId);
    return hit?.label || selectedRoomId || "";
  }, [roomOptions, selectedRoomId]);

  const hasMore = messages.length < total;
  const canExport =
    Boolean(selectedUnitId) && exportDates.length > 0 && exportMsgtypes.length > 0;

  const exportAfterTimeValue = useMemo(() => {
    if (!exportAfterTime) return null;
    const match = /^(\d{1,2}):(\d{2})$/.exec(exportAfterTime.trim());
    if (!match) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return Dayjs().hour(hour).minute(minute).second(0).millisecond(0);
  }, [exportAfterTime]);

  const loadUnits = useCallback(async () => {
    setUnitsLoading(true);
    try {
      const list = await trpc.fadan.businessUnits.list.query();
      setUnits(list);
      if (selectedUnitId && !list.some((unit: AnyRecord) => unit.id === selectedUnitId)) {
        setSelectedUnitId(undefined);
        localStorage.removeItem(GROUP_CHAT_UNIT_KEY);
        setSelectedRoomId(undefined);
      }
    } catch (error: any) {
      message.error(error?.message || "加载业务单元失败");
    } finally {
      setUnitsLoading(false);
    }
  }, [message, selectedUnitId]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  const fetchMessages = useCallback(
    async (nextPage: number, mode: "replace" | "prepend") => {
      if (!selectedRoomId) return;
      if (mode === "replace") {
        setLoading(true);
        stickToBottomRef.current = true;
      } else {
        setLoadingMore(true);
        if (scrollRef.current) {
          prevScrollHeightRef.current = scrollRef.current.scrollHeight;
        }
      }
      try {
        const result = await trpc.fadan.messages.query({
          roomId: selectedRoomId,
          page: nextPage,
          limit: PAGE_LIMIT,
          startTime: dateRange.startTime,
          endTime: dateRange.endTime,
        });
        const pageItems = sortByTimeAsc(result.items || []);
        setTotal(result.total || 0);
        setPage(result.page || nextPage);
        setMessages((prev) =>
          mode === "replace" ? pageItems : mergeUniqueById(pageItems, prev)
        );
      } catch (error: any) {
        message.error(error?.message || "加载群消息失败");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [message, selectedRoomId, dateRange.startTime, dateRange.endTime]
  );

  useEffect(() => {
    if (!selectedRoomId) {
      setMessages([]);
      setTotal(0);
      setPage(1);
      return;
    }
    fetchMessages(1, "replace");
  }, [selectedRoomId, dateRange.startTime, dateRange.endTime, fetchMessages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      return;
    }
    if (prevScrollHeightRef.current > 0) {
      const delta = el.scrollHeight - prevScrollHeightRef.current;
      el.scrollTop = Math.max(0, delta);
      prevScrollHeightRef.current = 0;
    }
  }, [messages]);

  const handleUnitChange = (value: string | undefined) => {
    setSelectedUnitId(value);
    if (value) {
      localStorage.setItem(GROUP_CHAT_UNIT_KEY, value);
    } else {
      localStorage.removeItem(GROUP_CHAT_UNIT_KEY);
    }
    setSelectedRoomId(undefined);
    setMessages([]);
    setTotal(0);
    setPage(1);
  };

  const handleMessageDateChange = (value: DayjsType | null) => {
    setMessageDate(value);
    setMessages([]);
    setTotal(0);
    setPage(1);
  };

  const handleLoadOlder = () => {
    if (!hasMore || loadingMore || loading) return;
    stickToBottomRef.current = false;
    fetchMessages(page + 1, "prepend");
  };

  const handleRefresh = () => {
    if (!selectedRoomId) return;
    fetchMessages(1, "replace");
  };

  const handleExport = async () => {
    if (!selectedUnitId || exportDates.length === 0) return;
    setExporting(true);
    try {
      const result = await trpc.fadan.exportGroupChatSpeakers.mutate({
        businessUnitId: selectedUnitId,
        dates: exportDates,
        afterTime: exportAfterTime || undefined,
        msgtypes: exportMsgtypes,
      });
      downloadBase64Xlsx(result.base64, result.fileName);
      message.success(
        `已导出 ${result.speakerCount} 人 / ${result.rowCount} 行（${result.roomCount} 个群）`
      );
      setExportModalOpen(false);
    } catch (error: any) {
      message.error(error?.message || "导出失败");
    } finally {
      setExporting(false);
    }
  };

  const emptyDescription = messageDate
    ? `${messageDate.format("YYYY-MM-DD")} 暂无存档消息（真实用户）`
    : "该群暂无存档消息（真实用户）";

  return (
    <div className="fade-in group-chat-page">
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            群聊天记录
          </Title>
          <Text type="secondary">
            仅展示存档中的真实用户发言（ArchiveMessage，不含白名单/气氛号）。发送人昵称、会员
            UID、WXID、头像来自发言名单（Guzi）。
          </Text>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="primary"
            disabled={!selectedUnitId}
            onClick={() => setExportModalOpen(true)}
          >
            导出
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={selectedRoomId ? handleRefresh : loadUnits}
            loading={loading || unitsLoading}
          >
            刷新
          </Button>
        </div>
      </div>

      <div className="group-chat-layout">
        <Card
          variant="borderless"
          className="shadow-sm group-chat-filter-card"
          title="筛选"
        >
          <div className="group-chat-filter-fields">
            <div className="group-chat-filter-field">
              <Text type="secondary" className="group-chat-filter-label">
                业务单元
              </Text>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="选择业务单元"
                style={{ width: "100%" }}
                loading={unitsLoading}
                value={selectedUnitId}
                options={units.map((unit) => ({ label: unit.name, value: unit.id }))}
                onChange={handleUnitChange}
              />
            </div>
            <div className="group-chat-filter-field">
              <Text type="secondary" className="group-chat-filter-label">
                群聊（预览）
              </Text>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={selectedUnitId ? "选择群聊" : "请先选择业务单元"}
                style={{ width: "100%" }}
                disabled={!selectedUnitId}
                value={selectedRoomId}
                options={roomOptions}
                onChange={(value) => setSelectedRoomId(value || undefined)}
              />
            </div>
            <div className="group-chat-filter-field">
              <Text type="secondary" className="group-chat-filter-label">
                消息日期（预览）
              </Text>
              <DatePicker
                allowClear
                value={messageDate}
                onChange={handleMessageDateChange}
                placeholder="消息日期"
                style={{ width: "100%" }}
                disabledDate={(current) => Boolean(current && current.isAfter(Dayjs(), "day"))}
              />
            </div>
          </div>
        </Card>

        <Card
          variant="borderless"
          className="shadow-sm group-chat-panel-card"
          styles={{
            body: {
              padding: 0,
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          }}
        >
          {!selectedRoomId ? (
            <div className="group-chat-empty">
              <Empty description="请先选择业务单元和群，再查看消息" />
            </div>
          ) : (
            <div className="group-chat-panel">
              <div className="group-chat-header">
                <div className="group-chat-header-title">
                  {selectedRoomLabel}
                  {messageDate ? (
                    <span className="group-chat-header-date">
                      {" · "}
                      {messageDate.format("YYYY-MM-DD")}
                    </span>
                  ) : (
                    <span className="group-chat-header-date"> · 全部日期</span>
                  )}
                </div>
                <Text type="secondary" className="group-chat-header-meta">
                  共 {total} 条存档消息
                </Text>
              </div>
              <div className="group-chat-scroll" ref={scrollRef}>
                {hasMore ? (
                  <div className="group-chat-load-more">
                    <Button size="small" loading={loadingMore} onClick={handleLoadOlder}>
                      加载更早消息
                    </Button>
                  </div>
                ) : null}
                {loading && messages.length === 0 ? (
                  <div className="group-chat-loading">
                    <Spin />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="group-chat-empty-inline">
                    <Empty description={emptyDescription} />
                  </div>
                ) : (
                  messages.map((item, index) => {
                    const prev = index > 0 ? messages[index - 1] : null;
                    const currentTs = new Date(item.messageTime || 0).getTime();
                    const prevTs = prev ? new Date(prev.messageTime || 0).getTime() : 0;
                    const showTime =
                      !prev || !Number.isFinite(prevTs) || currentTs - prevTs > TIME_GAP_MS;
                    const nickname =
                      item.senderNickname || item.senderName || item.senderId || "未知用户";
                    const mediaUrl = archiveMediaUrl(item);
                    const body = messageBodyText(item);
                    const metaParts: string[] = [];
                    if (item.taokeId) metaParts.push(`会员UID ${item.taokeId}`);
                    if (item.wxid) metaParts.push(`WXID ${item.wxid}`);
                    if (item.senderId) metaParts.push(`ID ${item.senderId}`);

                    return (
                      <div key={item.id || item.msgid} className="group-chat-item">
                        {showTime ? (
                          <div className="group-chat-time-divider">
                            {formatMessageTime(item.messageTime)}
                          </div>
                        ) : null}
                        <div className="group-chat-row">
                          <Avatar
                            size={40}
                            src={item.senderAvatar || undefined}
                            icon={<UserOutlined />}
                            className="group-chat-avatar"
                          />
                          <div className="group-chat-body">
                            <div className="group-chat-meta">
                              <span className="group-chat-nickname">{nickname}</span>
                              {metaParts.map((part) => (
                                <span key={part} className="group-chat-meta-chip">
                                  {part}
                                </span>
                              ))}
                              {!item.inSpeakerRoster ? (
                                <span className="group-chat-meta-chip group-chat-meta-chip--warn">
                                  未入名单
                                </span>
                              ) : null}
                            </div>
                            <div className="group-chat-bubble">
                              {mediaUrl ? (
                                <button
                                  type="button"
                                  className="group-chat-media-button"
                                  onClick={() =>
                                    setMediaModal({
                                      url: mediaUrl,
                                      title: body || nickname,
                                    })
                                  }
                                >
                                  <img
                                    src={mediaUrl}
                                    alt={body || "媒体"}
                                    className="group-chat-media"
                                    loading="lazy"
                                  />
                                </button>
                              ) : null}
                              {body ? (
                                <div className="group-chat-text">{body}</div>
                              ) : mediaUrl ? null : (
                                <div className="group-chat-text group-chat-text--muted">
                                  [{item.msgtype || "unknown"}]
                                  {item.mediaKey ? ` ${item.mediaKey}` : " 无文本内容"}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      <Modal
        open={exportModalOpen}
        title="导出发言名单"
        onCancel={() => !exporting && setExportModalOpen(false)}
        onOk={handleExport}
        okText="导出 xlsx"
        cancelText="取消"
        confirmLoading={exporting}
        okButtonProps={{ disabled: !canExport }}
        width={440}
      >
        <div className="group-chat-export-modal-body">
          <Text type="secondary" className="group-chat-export-hint">
            导出当前业务单元下全部群。所选日期内，同一群中用户每一天均需有达标发言（按所选消息类型与可选时间门槛）。每个用户一行：消息列时间与正文可换行；勾选图片时另有「图片」列嵌入阿里云缩略图（同列纵向堆叠，非单元格文本流）。
          </Text>
          <div className="group-chat-filter-field">
            <Text type="secondary" className="group-chat-filter-label">
              业务单元
            </Text>
            <Text>
              {selectedUnit?.name || selectedUnitId || "未选择"}
            </Text>
          </div>
          <div className="group-chat-filter-field">
            <Text type="secondary" className="group-chat-filter-label">
              导出日期
            </Text>
            <DateMultiPicker value={exportDates} onChange={setExportDates} />
          </div>
          <div className="group-chat-filter-field">
            <Text type="secondary" className="group-chat-filter-label">
              消息类型
            </Text>
            <Checkbox.Group
              options={[
                { label: "文字", value: "text" },
                { label: "图片", value: "image" },
              ]}
              value={exportMsgtypes}
              onChange={(values) => {
                setExportMsgtypes(values as Array<"text" | "image">);
              }}
            />
            <Text type="secondary" className="group-chat-export-time-hint">
              仅统计并导出勾选类型；默认仅文字。勾选图片时会从阿里云拉取缩略图写入 xlsx
            </Text>
          </div>
          <div className="group-chat-filter-field">
            <Text type="secondary" className="group-chat-filter-label">
              每日发言时间门槛（可选）
            </Text>
            <TimePicker
              allowClear
              format="HH:mm"
              needConfirm={false}
              value={exportAfterTimeValue}
              onChange={(value) => {
                setExportAfterTime(value ? value.format("HH:mm") : null);
              }}
              placeholder="不限当日时刻"
              style={{ width: "100%" }}
            />
            <Text type="secondary" className="group-chat-export-time-hint">
              {exportAfterTime
                ? `仅统计 ${exportAfterTime} 及之后的发言`
                : "不限当日时刻（全日任意发言均计）"}
            </Text>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!mediaModal}
        title={mediaModal?.title || "媒体预览"}
        onCancel={() => setMediaModal(null)}
        footer={null}
        width="min(900px, 92vw)"
        centered
      >
        {mediaModal ? (
          <div className="dispatch-media-preview-modal">
            <img
              src={mediaModal.url}
              alt={mediaModal.title}
              className="dispatch-media-preview-modal-image"
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
